import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TbClipboardText, TbEye, TbRefresh, TbShare } from 'react-icons/tb';
import { FaCheck } from 'react-icons/fa6';
import { ApproveToast } from '../ApproveToast';
// @ts-ignore
import toast from 'react-hot-toast';
import {
  useApprove,
  useApproveAIFM,
  useApproveAllFunds,
  useCheckAIFOrAIFM,
  useGetApprovalAIFMStatus,
  useGetApprovalAllFunds,
  useGetApprovalStatus
} from '../../../../services/report.service';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApproverDrawer } from '../../../Project/Quarters/ApproverDrawer';
import { PermissionGuard } from '../../../../components/PermissionGuard';
// @ts-ignore
import reportStore from '../../store';
import { Badge, Button, Flex, Tooltip } from '@mantine/core';
import { useAllComments } from '../../../../services/comment.service';
import { useUserGroupsCurrentUserAdminState } from '../../../../services/user.service';
import { permissionPolicies } from '../../../../permissions/policies';
import { idCreator } from '../../../Project/utils/idCreator';

interface HeaderProps {
  id?: string;
  onOpen: () => void;
  onOpenUpdateReport: () => void;
  isAIF001?: boolean;
  links?: Array<{ name: string; [key: string]: any }>;
  isViewer?: boolean;
  isMultiFunds?: boolean;
  isLoadingProject?: boolean;
  isRefetchingPeriod?: boolean;
  /** True while a report regeneration is running server-side
   *  (period.is_force_update_in_progress === true). Blocks the
   *  "Regenerate Report" button so the user can't queue a duplicate job
   *  and can't open the Update Report modal until the current run
   *  finishes. The flag is flipped by the same backend flow that emits
   *  the AutomationReportTaskUpdate SignalR event, so with the report
   *  page's SignalR-driven refetch it stays in sync in near-real-time. */
  isReportInProgress?: boolean;
  assignedTo?: any;
  extraActions?: React.ReactNode | null;
  isFormPF?: boolean;
  onBeforeAction?: (action: () => void) => void;
}

export function Header({
  id,
  onOpen,
  onOpenUpdateReport,
  isAIF001,
  links,
  isViewer = false,
  isMultiFunds,
  isLoadingProject,
  isRefetchingPeriod = false,
  isReportInProgress = false,
  assignedTo,
  extraActions = null,
  isFormPF = false,
  onBeforeAction
}: HeaderProps) {
  const { periodId, workflowId, reportingMemberState } = useParams<{
    periodId?: string;
    workflowId?: string;
    reportingMemberState?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpenApprover, setIsOpenApprover] = useState<boolean>(false);
  const isFCAReport = location?.pathname?.startsWith('/report/fca');
  const { data: currentUser } = useUserGroupsCurrentUserAdminState();
  const { data: checkAIFOrAIFM } = useCheckAIFOrAIFM({
    params: {
      period_id: periodId
    },
    queryParams: {
      enabled: isViewer
    }
  });

  const { isLockedPeriod } = reportStore;

  const onOpenApprover = (): void => {
    setIsOpenApprover(true);
  };

  const onCloseApprover = (): void => {
    setIsOpenApprover(false);
  };

  useEffect(() => {
    if (searchParams.get('openApprovalDrawer') === 'true') {
      setIsOpenApprover(true);
      setSearchParams(init => {
        init.delete('openApprovalDrawer');
        return init;
      });
    }
  }, [searchParams.get('openApprovalDrawer')]);

  const {
    data: approvalAllFundsStatus,
    refetch: approvalAllFundsStatusRefetch,
    isFetching: approvalAllFundsStatusIsFetching
  } = useGetApprovalAllFunds({
    params: {
      period_id: periodId
    },
    queryParams: {
      enabled: isViewer && !isAIF001 && isMultiFunds
    }
  });

  const approvalStatusAIF = useGetApprovalStatus({
    params: {
      period_id: periodId,
      fund_id: searchParams.get('fund_id')
    },
    queryParams: {
      enabled: isViewer && !isAIF001 && !isMultiFunds
    }
  });

  const approvalStatusAIFM = useGetApprovalAIFMStatus({
    params: {
      period_id: periodId
    },
    queryParams: {
      // AIFM has no per-fund concept, so the outer "multi-AIF view"
      // toggle must NOT gate this query. Otherwise, flipping multi-view
      // on for AIFs and then switching to the AIFM tab leaves this
      // disabled — the button never picks up the post-approval
      // is_approved flip and stays enabled.
      enabled: isViewer && isAIF001
    }
  });

  const approvalAllFundsStatusOne = (approvalAllFundsStatus as any)?.find(
    (item: any) => item.is_approve_required && !item.is_approved
  );
  const hasApprovableFunds = ((approvalAllFundsStatus as any) || []).some(
    (item: any) => item?.is_approve_required
  );
  const multiFundApprovalData =
    approvalAllFundsStatusOne ??
    (hasApprovableFunds ? { is_approved: true } : ((approvalAllFundsStatus as any)?.[0] ?? null));

  const approvalStatus = isAIF001
    ? approvalStatusAIFM
    : isMultiFunds
      ? {
          data: multiFundApprovalData,
          refetch: approvalAllFundsStatusRefetch,
          isFetching: approvalAllFundsStatusIsFetching
        }
      : approvalStatusAIF;

  const approveReqAIF = useApprove();
  const approveReqAIFM = useApproveAIFM();
  const approveAllFunds = useApproveAllFunds();

  const approveReq = isAIF001 ? approveReqAIFM : isMultiFunds ? approveAllFunds : approveReqAIF;

  const onConfirmApprove = (): void => {
    approveReq.mutate(
      {
        params: {
          period_id: periodId,
          fund_id: isMultiFunds ? undefined : searchParams.get('fund_id')
        }
      },
      {
        onSuccess: () => {
          toast.remove();
          approvalStatus.refetch();
          toast.success('Report was approved');
          setTimeout(() => {
            toast.remove();
          }, 2500);
        }
      }
    );
  };

  const onApprove = (): void => {
    toast.custom(
      t => (
        <ApproveToast
          isAIFM={isAIF001}
          onConfirmApprove={onConfirmApprove}
          isLoading={approveReq.isLoading}
          isMultiFunds={isMultiFunds}
        />
      ),
      {
        duration: 1000000
      }
    );
  };

  const { data: comments = [] } = useAllComments({
    queryOptions: {
      cacheTime: 0,
      staleTime: 0
    },
    params: {
      workflow_id: workflowId,
      fund_id: isAIF001
        ? undefined
        : isMultiFunds
          ? undefined
          : (searchParams.get('fund_id') === 'AIF001' ? undefined : searchParams.get('fund_id')) ||
            undefined,
      period_id: periodId
    },
    customKey: 'REPORT_COMMENTS'
  });

  const isFundRelevant = useCallback(
    (item: any): boolean => {
      const isFCAFund = item.fund_name === 'AIFM_FCA_UK';
      const isESMAFund = item.fund_name === 'AIFM_ESMA';

      if (isAIF001) {
        return isFCAReport ? isFCAFund : isESMAFund;
      }

      return !isFCAFund && !isESMAFund;
    },
    [isAIF001, isFCAReport]
  );

  const unresolvedRelevantComments = useMemo<any[]>(() => {
    return comments.filter(comment => !comment.is_resolved && isFundRelevant(comment));
  }, [comments, isFundRelevant]);

  // Per-fund reviewer privilege — reads `is_approve_required` off whichever
  // endpoint feeds the current view:
  //   • single-fund   → `useGetApprovalStatus({ period_id, fund_id })`
  //   • multi-AIF     → `useGetApprovalAllFunds({ period_id })` (array)
  //   • AIF001 (AIFM) → reviewer-wide `useCheckAIFOrAIFM`, no per-fund concept.
  //
  // NOTE: does NOT gate on the reviewer-wide `is_aif_required` flag from
  // `useCheckAIFOrAIFM`. That flag was false whenever every assigned fund
  // was view-only, which suppressed the whole reviewer section and left the
  // header blank — the "I see nothing" report. Reading straight off the
  // per-fund endpoint means view-only reviewers see the "View only" badge
  // and approve-privileged reviewers see the Approve button.
  const hasFundData = useMemo<boolean>(() => {
    if (!isViewer) return false;
    if (isAIF001) return !!(checkAIFOrAIFM as any); // AIFM check is reviewer-wide.
    if (isMultiFunds) return ((approvalAllFundsStatus as any) || []).length > 0;
    return !!(approvalStatusAIF as any)?.data;
  }, [
    isViewer,
    isAIF001,
    isMultiFunds,
    checkAIFOrAIFM,
    approvalAllFundsStatus,
    (approvalStatusAIF as any)?.data
  ]);

  const currentFundIsApproveRequired = useMemo<boolean>(() => {
    if (!isViewer) return false;
    if (isAIF001) return !!(checkAIFOrAIFM as any)?.is_aifm_required;
    if (isMultiFunds) {
      const list = (approvalAllFundsStatus as any) || [];
      return list.some((f: any) => f?.is_approve_required);
    }
    return !!(approvalStatusAIF as any)?.data?.is_approve_required;
  }, [
    isViewer,
    isAIF001,
    isMultiFunds,
    checkAIFOrAIFM,
    approvalAllFundsStatus,
    (approvalStatusAIF as any)?.data
  ]);

  const showViewOnlyBadge = isViewer && hasFundData && !currentFundIsApproveRequired;
  const showApproveButton = isViewer && hasFundData && currentFundIsApproveRequired;

  const canManageWorkflow = permissionPolicies.checkAccess({
    currentUserRole: (currentUser as any)?.role,
    currentUserId: (currentUser as any)?.azure_object_id,
    assignedTo
  });

  const onCloseComparisionMode = (): void => {
    reportStore.setIsComparisonReport(false);
    onOpen();
  };

  const runBeforeAction = (action?: () => void): void => {
    if (onBeforeAction) {
      onBeforeAction(action || (() => {}));
      return;
    }

    action?.();
  };

  const approveButtonLabel = useMemo<string>(() => {
    if (approvalStatus?.data?.is_approved) return 'Approved';

    const isUS = reportingMemberState === 'us';

    // AIFM has no per-fund concept — the "multi-AIF view" toggle
    // shouldn't influence the AIFM label. Check isAIF001 first so
    // switching to AIFM tab while multi-view is on doesn't read
    // "Approve All AIFM Reports".
    if (isAIF001) return isUS ? 'Approve Managers Report' : 'Approve AIFM Report';

    if (isMultiFunds) return isUS ? 'Approve All Funds Reports' : 'Approve All AIF Reports';

    return isUS ? 'Approve Fund Report' : 'Approve AIF Report';
  }, [approvalStatus?.data?.is_approved, isAIF001, isMultiFunds, reportingMemberState]);

  return (
    <>
      <Flex
        id={id ?? 'report-header-actions'}
        justify="flex-end"
        align="center"
        bg="#f8f9fa"
        p="16px 16px 0"
      >
        {!isViewer ? (
          <Flex id="report-header-actions-group" align="center" gap="10px">
            {(isFormPF || (!isAIF001 && !isLockedPeriod)) && (
              <PermissionGuard allowed={canManageWorkflow}>
                <Button
                  id="report-header-regenerate-report-button"
                  variant="subtle"
                  size="sm"
                  leftSection={<TbRefresh />}
                  onClick={() => runBeforeAction(onOpenUpdateReport)}
                  w="auto"
                  loading={isRefetchingPeriod}
                  disabled={isReportInProgress}
                  title={
                    isReportInProgress ? 'A report regeneration is already in progress.' : undefined
                  }
                >
                  Regenerate Report
                </Button>
              </PermissionGuard>
            )}
            <Button
              id="report-header-static-template-button"
              variant="subtle"
              size="sm"
              leftSection={<TbClipboardText />}
              onClick={() => runBeforeAction(onCloseComparisionMode)}
            >
              Static Template
            </Button>
            {!isLockedPeriod && (
              <PermissionGuard allowed={canManageWorkflow}>
                <Button
                  id="report-header-share-button"
                  size="sm"
                  variant="subtle"
                  onClick={() => runBeforeAction(onOpenApprover)}
                  leftSection={<TbShare />}
                >
                  Share
                </Button>
              </PermissionGuard>
            )}
            {extraActions}
          </Flex>
        ) : showApproveButton ? (
          <Button
            id={`report-header-approve-button-${idCreator(approveButtonLabel)}`}
            disabled={
              approvalStatus?.data?.is_approved ||
              approvalStatus.isFetching ||
              unresolvedRelevantComments.length > 0
            }
            onClick={onApprove}
            color="green.6"
            size="sm"
            rightSection={<FaCheck />}
          >
            {approveButtonLabel}
          </Button>
        ) : showViewOnlyBadge ? (
          <Tooltip
            label="You've been assigned to this fund for view-only. Approval isn't part of your privileges."
            withArrow
            multiline
            maw={280}
            position="bottom"
          >
            <Badge
              size="lg"
              radius="sm"
              variant="light"
              color="gray"
              leftSection={<TbEye size={14} />}
              styles={{ label: { textTransform: 'none', fontWeight: 500 } }}
            >
              View only
            </Badge>
          </Tooltip>
        ) : (
          ''
        )}
      </Flex>
      <ApproverDrawer
        id="report-header-approver-drawer"
        isOpen={isOpenApprover}
        onClose={onCloseApprover}
        periodId={isOpenApprover ? periodId : null}
        currentUser={currentUser}
        selectedPeriod={undefined}
      />
    </>
  );
}
